import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { API, graphqlOperation } from "aws-amplify";
import { listParts } from "../../graphql/queries";
import { createInvoice } from "../../graphql/mutations";
import { useInvoice } from "../../contexts/InvoiceContext";
import { useTheme } from "@mui/material/styles";
import { AppContext } from "../../App";
import SearchPricelist from "../search/SearchPricelist";

// PartCard component for displaying parts in the invoice form
function PartCard({
  part,
  selected,
  onSelect,
  onQuantityChange,
  quantity,
  theme,
}) {
  const partNumber =
    part["PART NUMBER"] || part.partNumber || part.part_number || "";
  const price =
    part.finalPrice ||
    part.groupAdjustedPrice ||
    part.price ||
    part.minPrice ||
    part["PRICE"] ||
    0;
  const image1 = part.image1 || part.image || null;

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: selected ? "rgba(33, 150, 243, 0.1)" : "#1a1a1a",
        border: selected
          ? `1px solid ${theme.palette.primary.main}`
          : "1px solid #333",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          backgroundColor: selected ? "rgba(33, 150, 243, 0.15)" : "#2a2a2a",
        },
      }}
      onClick={() => onSelect(part)}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={2}>
          {image1 && (
            <img
              src={
                image1.startsWith("http")
                  ? image1
                  : `${process.env.PUBLIC_URL}/${image1}`
              }
              alt={partNumber}
              style={{
                width: "60px",
                height: "60px",
                objectFit: "contain",
                borderRadius: "5px",
              }}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main }}
          >
            {partNumber}
          </Typography>
          <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
            ${Math.round(parseFloat(price))}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(Math.max(0, quantity - 1));
              }}
            >
              <RemoveIcon
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ minWidth: "30px", textAlign: "center" }}
            >
              {quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(quantity + 1);
              }}
            >
              <AddIcon
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

// PartsSearchDialog component for searching and selecting parts with customer pricing
function PartsSearchDialog({
  open,
  onClose,
  onPartSelect,
  customerPricelist,
  customerData,
  theme,
}) {
  if (!customerData) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#000000",
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#1a1a1a",
            borderBottom: `1px solid ${theme.palette.primary.main}`,
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SearchIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: theme.palette.primary.main }}
            >
              Search and Select Parts
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: theme.palette.primary.main }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#000000", pt: 2 }}>
          <Typography color="error">
            No customer data available. Please select a customer first.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#1a1a1a",
            borderTop: `1px solid ${theme.palette.primary.main}`,
            pt: 1,
          }}
        >
          <Button onClick={onClose} sx={{ color: "#D3D3D3" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <SearchPricelist
      defaultSearch=""
      onPartSelect={onPartSelect}
      showPricelistSelect={false}
      userRecordOverride={customerData}
      csvData={null}
      onLoadPricelistData={(pricelistId) => {
        console.log("Loading pricelist data:", pricelistId);
      }}
      pricelistId={customerData.activePricelistID}
    />
  );
}

export default function InvoiceForm({
  customerData,
  pricelistData,
  onSuccess,
  onError,
}) {
  const theme = useTheme();
  const { setCurrentCustomer, setCurrentPricelistName } = useInvoice();
  const { userRecord } = useContext(AppContext);
  const [dateSent, setDateSent] = useState(
    new Date().toISOString(),
  );
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [status, setStatus] = useState("Pending");
  const [name, setName] = useState("");
  const [parts, setParts] = useState([]);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (customerData) {
      setCurrentCustomer(customerData);
      if (customerData.companyName) {
        setName(customerData.companyName);
      }
    }
  }, [customerData, setCurrentCustomer]);

  useEffect(() => {
    if (pricelistData?.name) {
      setCurrentPricelistName(pricelistData.name);
    }
  }, [pricelistData, setCurrentPricelistName]);

  const calculateTotal = () => {
    return parts.reduce((total, part) => {
      const price =
        part.finalPrice ||
        part.groupAdjustedPrice ||
        part.price ||
        part.minPrice ||
        part["PRICE"] ||
        0;
      return total + price * part.quantity;
    }, 0);
  };

  const handleAddParts = (part, grade) => {
    setParts((prevParts) => {
      const newParts = [...prevParts];
      const partNumber =
        part["PART NUMBER"] || part.partNumber || part.part_number || "";
      const existingPart = newParts.find((p) => {
        const existingPartNumber =
          p["PART NUMBER"] || p.partNumber || p.part_number || "";
        return existingPartNumber === partNumber;
      });

      if (existingPart) {
        const quantityToAdd = grade !== undefined && grade !== null ? grade : 1;
        existingPart.quantity += quantityToAdd;
      } else {
        const initialQuantity =
          grade !== undefined && grade !== null ? grade : 1;
        newParts.push({ ...part, quantity: initialQuantity });
      }
      return newParts;
    });
  };

  const getPartNumber = (part) => {
    return part["PART NUMBER"] || part.partNumber || part.part_number || "";
  };

  const handleRemovePart = (part) => {
    const partNumber = getPartNumber(part);
    setParts((prevParts) =>
      prevParts.filter((p) => getPartNumber(p) !== partNumber),
    );
  };

  const handleQuantityChange = (part, quantity) => {
    const partNumber = getPartNumber(part);
    setParts((prevParts) =>
      prevParts.map((p) =>
        getPartNumber(p) === partNumber
          ? { ...p, quantity: Math.max(0, quantity) }
          : p,
      ),
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!dateSent) newErrors.dateSent = "Date is required";
    if (!name && !customerData?.companyName) {
      newErrors.name = "Name is required";
    }
    if (parts.length === 0) newErrors.parts = "At least one part is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    console.log("Submitting invoice...");
    setLoading(true);
    try {
      const invoiceData = {
        dateSent,
        invoiceNumber: invoiceNumber || `INV-${Date.now()}`,
        status,
        name:
          name || customerData?.companyName || customerData?.email || "Unknown",
        partCount: parts.length,
        ownerID: userRecord?.id,
        recipientID: customerData?.id,
      };

      console.log("Invoice data to create:", invoiceData);

      await API.graphql({
        query: createInvoice.replaceAll("__typename", ""),
        variables: {
          input: invoiceData,
        },
      });

      console.log("Invoice created successfully!");
      if (onSuccess) {
        onSuccess({
          ...invoiceData,
          total: calculateTotal(),
        });
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      if (onError) {
        onError(
          { message: error.message },
          error.errors?.map((e) => e.message).join("\n") ||
            "Failed to create invoice",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "#1a1a1a",
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: theme.palette.primary.main, mb: 2 }}
        >
          Customer Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
              <strong>Customer:</strong>{" "}
              {customerData?.companyName || customerData?.email || "Unknown"}
            </Typography>
            {customerData?.phone && (
              <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
                <strong>Phone:</strong> {customerData.phone}
              </Typography>
            )}
            {customerData?.address && (
              <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
                <strong>Address:</strong> {customerData.address}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
              <strong>Pricelist:</strong>{" "}
              {pricelistData?.name || "No pricelist assigned"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#D3D3D3" }}>
              <strong>Parts Count:</strong> {pricelistData?.validRows || 0}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#1a1a1a",
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.primary.main, mb: 2 }}
              >
                Invoice Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date Sent"
                    type="datetime-local"
                    value={dateSent}
                    onChange={(e) => setDateSent(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dateSent}
                    helperText={errors.dateSent}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: theme.palette.primary.main,
                        "& fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Invoice Number"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder={`INV-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}${String(new Date().getDate()).padStart(2, "0")}`}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: theme.palette.primary.main,
                        "& fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: theme.palette.primary.main,
                        "& fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Sent">Sent</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: theme.palette.primary.main,
                        "& fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <PartsSearchDialog
              open={searchDialogOpen}
              onClose={() => setSearchDialogOpen(false)}
              onPartSelect={handleAddParts}
              customerPricelist={pricelistData}
              customerData={customerData}
              theme={theme}
            />

            <Paper
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: "#1a1a1a",
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.primary.main }}
                >
                  Parts ({parts.length})
                </Typography>
              </Box>

              {parts.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: `2px dashed ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    color: "#666",
                  }}
                >
                  <Typography>No parts added yet</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click "Add Parts" to search and select parts for this
                    invoice
                  </Typography>
                </Box>
              ) : (
                <>
                  {parts.map((part) => {
                    const partNumber = getPartNumber(part);
                    return (
                      <PartCard
                        key={partNumber}
                        part={part}
                        selected={true}
                        onSelect={() => {}}
                        onQuantityChange={(qty) =>
                          handleQuantityChange(part, qty)
                        }
                        quantity={part.quantity}
                        theme={theme}
                      />
                    );
                  })}
                </>
              )}

              {errors.parts && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {errors.parts}
                </Typography>
              )}

              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#D3D3D3" }}>
                    Total:
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: theme.palette.primary.main, fontWeight: 700 }}
                  >
                    ${Math.round(calculateTotal())}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              sx={{
                p: 3,
                backgroundColor: "#1a1a1a",
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: theme.palette.primary.main, mb: 2 }}
              >
                Actions
              </Typography>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  fontWeight: 600,
                  py: 1.5,
                  mb: 2,
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                }}
              >
                {loading ? "Creating Invoice..." : "Create Invoice"}
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  setParts([]);
                  setInvoiceNumber("");
                  setStatus("Pending");
                  setName(customerData?.companyName || "");
                }}
                sx={{
                  color: "#D3D3D3",
                  border: `1px solid ${theme.palette.primary.main}`,
                  "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
                }}
              >
                Reset Form
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
